/**
 * Search result item — displays a single matching verse with highlighted words.
 */

import type { SearchResult } from '@skjv/common';
import { formatReference, type BibleLocation } from '@skjv/common';
import { bibleState } from '@/state/bible-state';

interface SearchResultItemProps {
  result: SearchResult;
  searchText: string;
}

/** Convert 1-based DB coordinates to 0-based BibleLocation. */
function toLocation(result: SearchResult): BibleLocation {
  return {
    testamentIndex: result.testament - 1,
    bookIndex: result.bookNumber - 1,
    chapterIndex: result.chapterNumber - 1,
    verseIndex: result.verseNumber - 1,
  };
}

/** Highlight matching words in the verse text. */
function highlightText(text: string, searchText: string): React.ReactNode[] {
  const words = searchText.trim().toUpperCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [text];

  // Build a regex matching any of the search words
  const escaped = words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(`(${escaped.join('|')})`, 'gi');
  const parts = text.split(pattern);

  return parts.map((part, i) => {
    const isMatch = words.some((w) => w === part.toUpperCase());
    return isMatch ? (
      <mark key={i} className="search-highlight">{part}</mark>
    ) : (
      <span key={i}>{part}</span>
    );
  });
}

/** Format verse text to title case for display. */
function toTitleCase(text: string): string {
  return text
    .toLowerCase()
    .replace(/(^|\.\s+|:\s+)([a-z])/g, (_, sep, char) => sep + char.toUpperCase())
    .replace(/^./, (c) => c.toUpperCase());
}

export function SearchResultItem({ result, searchText }: SearchResultItemProps) {
  const location = toLocation(result);
  const ref = formatReference(location);
  const displayText = toTitleCase(result.text);

  const handleClick = () => {
    bibleState.setCurrentLocation(location);
  };

  return (
    <button className="search-result-item" onClick={handleClick}>
      <span className="search-result-ref">{ref}</span>
      <span className="search-result-text">
        {highlightText(displayText, searchText)}
      </span>
    </button>
  );
}
